import { Alert } from '@televet/kibble-ui/Alert';
import { Flex } from '@televet/kibble-ui/chakra';
import { Text } from '@televet/kibble-ui/Text';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PageContainer } from '@/components/PageContainer';
import { trpc } from '@/lib/trpc';
import { ConceptCard } from '../-components/ConceptCard';

const RouteComponent = () => {
  const { data, isLoading, isError, error } = useQuery(
    trpc.petConditions.listConcepts.queryOptions()
  );

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Breadcrumbs
          items={[{ label: 'Pet Conditions', to: '/pet-conditions' }, { label: 'Concepts' }]}
        />

        <Flex direction="column" gap={1}>
          <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
            Condition concepts
          </Text>
          <Text variant="subtle" fontSize="sm">
            Read-only. What is installed and how much evidence it has produced, across every clinic
            in this environment. Voided signals are excluded. A concept with no evidence is not
            necessarily broken: it may have no source data yet.
          </Text>
        </Flex>

        {/* An error is not an empty pipeline, and saying so sends QA after the
            wrong bug. The route tree guards on the client only, so a non-admin
            reaches this procedure and gets FORBIDDEN. */}
        {isError && (
          <Alert
            status="error"
            hideCloseButton
            title="Could not load concepts"
            description={error?.message ?? 'The request failed.'}
          />
        )}

        {isLoading && (
          <Flex justify="center" p={8}>
            <Text variant="subtle">Loading concepts…</Text>
          </Flex>
        )}

        {!isLoading && !isError && data?.concepts.length === 0 && (
          <Flex justify="center" p={8}>
            <Text variant="subtle">No condition concepts are seeded in this environment.</Text>
          </Flex>
        )}

        {data?.truncated && (
          <Alert
            status="warning"
            hideCloseButton
            title="Not every concept is shown"
            description={`Showing ${data.concepts.length} of ${data.total}. The list is capped, so a concept later in the alphabet may be missing.`}
          />
        )}

        <Flex gap={4} wrap="wrap" align="stretch">
          {(data?.concepts ?? []).map((concept) => (
            <ConceptCard key={concept.id} concept={concept} />
          ))}
        </Flex>
      </Flex>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_authenticated/pet-conditions/concepts/')({
  component: RouteComponent,
});
